import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { RoutePath } from 'app/routing/routes';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { map, take } from 'rxjs/operators';
import { Observable, of, Subject, Subscription } from 'rxjs';

import { addSeconds, differenceInSeconds, format, isValid } from 'date-fns';
import { SessionStorage, SessionStorageService } from 'ngx-webstorage';

import { UserInfo, UserRole } from 'app/_models';
import { Timer, TimerService } from './timer/timer.service';
import { ConfigService } from './config.service';
import { BUILD_TIME, VERSION_NUMBER } from './version/version.service';
import { NotificationService } from './notification.service';
import { SplashService } from './splash/splash.service';
import { DialogsService } from './dialogs/dialogs.service';
import { BaseConfirmationDialogOptions } from './dialogs/confirmation/confirmation-dialog.component';
import { UserService } from './user.service';
import { ToolsService } from './tools.service';

export enum Role {
    Admin,
    Inspector,
}

export interface AuthPacket {
    AuthToken: string;
    DWAVersion: string;
}

export interface TestConnectionPacket {
    Status: boolean;
    DWAVersion: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
    // 17 minutes (time period to wait between successive checks of whether the auth token is recently used)
    static AUTHCHECKTIME: number = 17 * 60;
    // 17 minutes (time period to wait before logging the user out automatically). Roughly 20 minutes total between check
    // and autologout
    static LOGOUTWARNINGTIME = 170;

    private _subRelease: Subscription = new Subscription();
    private _authtimer: Timer = null;

    @SessionStorage() nextAuthRefresh: string;

    AuthTokenCancelFlag: boolean;
    AuthTokenRefreshHandle: number;
    AuthRefreshDeadline: Date;
    HasVersionUpdate: Subject<boolean> = new Subject<boolean>();
    BuildTime: Subject<string> = new Subject<string>();
    User: UserInfo;
    VersionNumber: Subject<string> = new Subject<string>();
    IsRefreshing: boolean;

    ReturnUrl = '';

    apiUrl: string;

    constructor(
        private http: HttpClient,
        private configService: ConfigService,
        private userService: UserService,
        private router: Router,
        private sessionStorage: SessionStorageService,
        private dialogService: DialogsService,
        private splashService: SplashService,
        private notificationService: NotificationService,
        private toolsService: ToolsService,
        private timerService: TimerService,
        @Inject('BASE_URL') baseUrl: string
    ) {
        // this.userSubject = new BehaviorSubject<UserInfo>(null);
        // this.user = this.userSubject.asObservable();
        this.apiUrl = baseUrl;

        this.AuthRefreshDeadline = this.nextAuthRefresh ? new Date(this.nextAuthRefresh) : null;
        this.IsRefreshing = false;

        const btime = new Date(BUILD_TIME);
        if (isValid(btime)) {
            this.BuildTime.next(format(btime, 'MMM. dd, yyyy h:mm t'));
        }

        this.VersionNumber.next(VERSION_NUMBER);

        // Set up a restartable timer for use in authtoken refreshing
        this._authtimer = this.timerService.CreateTimer('authtoken', 15 * 1000);

        if (this._authtimer) {
            this._subRelease.add(
                this._authtimer.Timer.subscribe((tick) => {
                    // Refresh the authtoken.
                    this.AuthTokenRefresh();
                })
            );
        }

        // Set up the subscription to watch for the user being updated, so we can tell if we need to boot up auth token refreshing.
        this._subRelease.add(
            this.userService.UserSubject.subscribe((user) => {
                this.User = user;

                // See if we have a User object already known to us. If so, this was a refresh of the page most likely,
                // and thus we need to kick start authtoken refreshing again.
                if (this.User && !this.IsRefreshing && this.User.authToken) {
                    // May not need this - we're not really recycling authtokens yet.
                    // this.StartAuthTokenRefreshing();
                }
            })
        );
    }

    ngOnDestroy() {
        if (this._authtimer) {
            this._authtimer.StopTimer();
            this._authtimer = null;
        }
        this._subRelease.unsubscribe();
    }

    // #region Auth Token Refresh Method

    private AuthTokenRefresh(checkRecent?: boolean) {
        const self = this;

        // Now retrieve the auth token for the currently logged in user, stored in the userService
        const auth = self.userService.User.authToken;

        // Send a request to the system to check to see if the user's "Recent Use" flag is set. If it is,
        // we will refresh their auth token and reset all countdown timers.
        self.CheckRecentUse(auth).subscribe(
            (recentUse) => {
                if (recentUse === true) {
                    // Call the service which will refresh the auth token.
                    self.RefreshAuthToken(auth).subscribe(
                        (newAuthPacket) => {
                            const newAuth = newAuthPacket.AuthToken;

                            if (newAuthPacket && newAuthPacket.DWAVersion) {
                                // We have version information. Does it match the version known here in the TypeScript?
                                // If not, we set the update flag.
                                if (VERSION_NUMBER !== newAuthPacket.DWAVersion) {
                                    // Mismatch. Set the flag.
                                    self.HasVersionUpdate.next(true);
                                }
                            }

                            // We have passed the check to stay using the system. Update the user variable in the
                            // userService to reflect this.
                            self.userService.UpdateAuthToken(newAuth);

                            // If there is no flag signal set preventing this loop from continuing, then we can set up
                            // the next iteration with a timeout now.
                            if (!self.AuthTokenCancelFlag) {
                                if (self._authtimer) {
                                    self._authtimer.StartTimer(AuthService.AUTHCHECKTIME * 1000);
                                }

                                // Set the refresh deadline app value
                                const Now = new Date();
                                self.AuthRefreshDeadline = addSeconds(Now, AuthService.AUTHCHECKTIME);
                                self.nextAuthRefresh = self.toolsService.toISOLocal(self.AuthRefreshDeadline);
                            }
                        },
                        (error) => {
                            // An error occurred. Attempt to retrieve it.
                            self.HandleServiceError(error, self.userService.User);
                        }
                    );
                } else {
                    // We either could not retrieve the "Recent Use" flag, or we DID and it was false. In either case,
                    // we must begin a countdown process to log out the user from the system.
                    // First, disable the current auth token refresh cycling. We won't restart that until we get
                    // feedback from the user.
                    self.CancelAuthTokenRefreshing();

                    self.dialogService
                        .OpenCountdownDialog({
                            TitleText: 'Session Inactivity Logout',
                            MessageText:
                                `You are about to be automatically logged out of the system due to inactivity. ` +
                                `If you wish to stay logged in to the site, please click the "Stay Logged In" button below.`,
                            OKText: 'Stay Logged In',
                            CancelText: 'Log Out',
                            CountdownTime: AuthService.LOGOUTWARNINGTIME,
                        })
                        .subscribe((res) => {
                            if (res === true) {
                                // User wants to stay logged in - Restart the auth token refresh cycling.
                                self.RefreshAuthToken(auth).subscribe(
                                    (newAuthPacket) => {
                                        const newAuth = newAuthPacket.AuthToken;

                                        if (newAuthPacket && newAuthPacket.DWAVersion) {
                                            // We have version information. Does it match the version known here in the TypeScript?
                                            // If not, set the update flag.
                                            if (VERSION_NUMBER !== newAuthPacket.DWAVersion) {
                                                // Mismatch. Set the flag.
                                                self.HasVersionUpdate.next(true);
                                            }
                                        }

                                        // We have a new valid auth token to use. Update the user object accordingly with the new token.
                                        self.userService.UpdateAuthToken(newAuth);
                                    },
                                    (error) => {
                                        let body = error.json();
                                        body =
                                            (body && body.Message) ||
                                            body ||
                                            `An error occurred while trying to validate your active connection to the system. ` +
                                                `You have been logged out.`;
                                        self.notificationService.showError(body);
                                    }
                                );
                            } else {
                                // User wishes to logout, or the timer expired. Either way, log them out.

                                // Now log them out.
                                self.Logout(false, 1);
                            }
                        });
                }
            },
            (error) => {
                self.CancelAuthTokenRefreshing();

                self.Logout(false);
            }
        );
    }

    // #endregion

    // #region Cancel Auth Token Refreshing Method

    CancelAuthTokenRefreshing() {
        // If possible, use the handle to cancel the timeout from happening
        if (this._authtimer) {
            this._authtimer.PauseTimer();
        }

        // Cancel the flag, handle, and deadline to refresh
        this.AuthTokenRefreshHandle = null;
        this.AuthTokenCancelFlag = null;
        this.AuthRefreshDeadline = null;
        this.IsRefreshing = false;
        this.nextAuthRefresh = null;
    }

    // #endregion

    // #region Change Impersonation Target Service Call

    ChangeImpersonationTarget(AuthToken: string, SAPID: string) {
        return this.http.get<UserInfo>(
            `${this.configService.hostAddress}` +
                `Api/Services/Authentication/ChangeImpersonationTarget?AuthToken=${AuthToken || ''}&SAPID=${SAPID || ''}`
        );
    }

    // #endregion

    // #region Check Recent Use Service Method - This method simply calls the database to see if the user has done anything

    CheckRecentUse(AuthToken: string) {
        return this.http.get<boolean>(this.configService.hostAddress + `api/Login/recentuse?AuthToken=${AuthToken || ''}`);
    }

    // #endregion

    // #region Get FIRE Version Number Service Call

    GetDWAVersionNumber() {
        return this.http.get<string>(`${this.configService.hostAddress}api/Login/version`);
    }

    // #endregion

    // #region Handle Service Error Service Call

    HandleServiceError(error: any, user?: UserInfo) {
        const self = this;

        let status: number = null;
        let msg: string = null;

        if (error instanceof HttpErrorResponse) {
            status = error.status;

            const body = error && error.error;
            msg =
                body.ExceptionMessage || body.Message || body || (typeof body === 'string' ? body : 'An unknown error has occurred.');
        } else {
            let errorPacket = error.message || error;
            if (errorPacket != null && errorPacket !== '') {
                errorPacket = JSON.parse(errorPacket);
            }
            msg =
                errorPacket != null && errorPacket instanceof Object
                    ? errorPacket.ExceptionMessage || errorPacket.Message || error
                    : errorPacket || 'An unknown error has occurred.';
        }

        this.splashService.CloseSplash();

        let handled = false;

        if (status === 401) {
            // This is an unauthorized response. Check the msg field. If it matches the string 'AuthToken Expired',
            // the user's authtoken has expired and the user needs to log back in.
            if (msg === 'AuthToken Expired') {
                // It's an expired authtoken. Set the flag that we've handled this error, and we'll do a relogin dialog.
                handled = true;

                // Cancel authtoken refreshing - pointless now anyway
                self.CancelAuthTokenRefreshing();

                // Pop up a dialog box to let the user log in again and stay on this screen.
                self.dialogService
                    .OpenLoginBoxDialog({
                        TitleText: 'Session Timed Out - Please Re-Login',
                        MessageText:
                            `Your current session to the District Website appears to have timed out due to inactivity or ` +
                            `network disconnection. Before you can continue, please login again using the login form ` +
                            `provided below.<br /><br /><strong><span style="color:red">Note: </span></strong>The operation you were ` +
                            `just attempting did not succeed, but your data on this page (if any) has not been lost. After you log ` +
                            `back in to the District Website, you may attempt the operation once again.`,
                        OKText: 'Login',
                        CancelText: 'Cancel',
                        Username: user && user.accountName,
                    })
                    .subscribe((credentials) => {
                        if (credentials != null) {
                            // Attempt to log back in using the provided credentials
                            self.RevalidateSession(
                                credentials.Username,
                                credentials.Password,
                                user && user.authToken,
                                user && user.sapid,
                                user != null ? user.isImpersonation : false
                            ).subscribe(
                                (userinfo) => {
                                    if (userinfo) {
                                        // We can now update the authtoken we just received for this user.
                                        self.userService.UpdateAuthToken(userinfo.authToken);

                                        // Toast successful revalidation
                                        self.splashService.SnackbarSuccess(
                                            'Session revalidated successfully. Please retry the last request.',
                                            6000
                                        );
                                    }
                                },
                                (rerror) => {
                                    self.HandleServiceError(rerror);
                                }
                            );
                        } else {
                            // Now logout.
                            self.Logout(false);
                        }
                    });
            }
        }

        if (!handled && status === 408) {
            // This is a Request Timeout response, indicating the user's session has expired.
            handled = true;

            this.dialogService
                .OpenMessageBoxDialog({
                    TitleText: 'Session Timed Out',
                    MessageText:
                        `Your current session to the Fire Entry Review Entry System has timed out and your login access has been closed. ` +
                        `In order to continue using the Fire Entry Review Entry System you will need to login again to the system.`,
                })
                .subscribe((res) => {
                    // Force an unconfirmed logout to clear user stored values and return to the login screen.
                    this.Logout(false, 2);
                });
        }

        if (!handled) {
            // Something else, which should be snackbar displayed.
            this.splashService.SnackbarError(msg, 8000);
        }
    }

    // #endregion

    // #region Impersonate User Service Method

    ImpersonateUser(
        AuthToken: string,
        CurrentRole: string,
        Username: string,
        ImpersonatorUserID: number,
        ImpersonatorSAPID: string,
        ImpersonatorPositionNo: string
    ) {
        return this.http
            .post<UserInfo>(this.configService.hostAddress + `Api/Services/Authentication/ImpersonateUser`, {
                AuthToken: AuthToken || '',
                CurrentRole: CurrentRole || '',
                Username: Username || '',
                ImpersonatorUserID: ImpersonatorUserID || '',
                ImpersonatorSAPID: ImpersonatorSAPID || '',
                ImpersonatorPositionNo: ImpersonatorPositionNo || '',
            })
            .pipe(
                map((userinfo) => {
                    // If we have roles, before we return we should number them so we can recover in case of page refreshing.
                    if (userinfo.roles && !!userinfo.roles.length) {
                        userinfo.roles.forEach((role, idx) => {
                            role.roleNumber = idx;
                        });
                    }

                    return userinfo;
                })
            );
    }

    // #endregion

    // #region Logout From System Service Call

    LogoutFromSystem(AuthToken: string, Reason?: number) {
        const url = this.configService.hostAddress + `api/auth/Logout`;

        return this.http.post<boolean>(url, { authToken: AuthToken });
    }

    // #endregion

    // #region Logout Service Call

    Logout(confirmation?: boolean, reason?: number) {
        const self = this;
        let confObs: Observable<boolean> = null;

        if (confirmation === true) {
            // Pop up a confirmation box to confirm desire to logout.
            const opts: BaseConfirmationDialogOptions = {
                TitleText: 'Confirmation Requested',
                MessageText: 'You are about to log out of the site. Are you sure you wish to exit at this time?',
                OKText: 'Yes - Log Out',
                CancelText: 'No - Stay Logged In',
            };

            confObs = self.dialogService.OpenConfirmationDialog(opts);
        } else {
            // No dialog needed.
            confObs = of<boolean>(true).pipe(take(1));
        }

        self._subRelease.add(
            confObs.subscribe((result) => {
                if (result === true) {
                    // User is opting to log out. Proceed.
                    self.LogoutFromSystem(self.User?.authToken, confirmation === true ? null : reason).subscribe();

                    // Stop authtoken refreshing
                    self.CancelAuthTokenRefreshing();

                    // Null out the current user.
                    self.userService.ClearUser();

                    // Snackbar the logout if this was user initiated (reason null or 0)
                    if (reason == null || reason === 0) {
                        self.splashService.SnackbarSuccess('Successfully logged out.', 5000);
                    }

                    // Close out all remaining open dialogs
                    self.dialogService.CloseAllDialogs();

                    // Navigate to the login state.
                    // self.router.navigate(['/login']);
                    // window.location.href = self.configService.hostAddress;
                    self.router.navigate([RoutePath.Login]);
                }
            })
        );
    }

    // #endregion

    // #region Process Login Service Method

    ProcessLogin(user: UserInfo) {
        const self = this;

        // Set the currently logged in user information.
        self.userService.SetUser(user);

        let startingRole = null as UserRole;

        if (user && user.roles && !!user.roles.length) {
            startingRole = user.roles[0];
        }

        if (startingRole) {
            self.userService.SetUserCurrentRole(startingRole);
        } else {
            // No role found. Reject politely.
            self.dialogService
                .OpenMessageBoxDialog({
                    TitleText: 'Unable to login',
                    MessageText:
                        `We're sorry, the District Website App is currently only available to select district staff ` +
                        `and other staff that have been established by the District Website App owners for access. ` +
                        `If this is an error, please contact the Help Desk for more assistance.`,
                })
                .subscribe(() => {
                    self.Logout(null, null);
                });
        }
    }

    // #endregion

    // #region Refresh Authentication Token Service Method

    RefreshAuthToken(AuthToken: string) {
        return this.http.post<AuthPacket>(this.configService.hostAddress + `api/Login/authrefresh`, {
            AuthToken: AuthToken || '',
        });
    }

    // #endregion

    // #region Revalidate Session Service Call

    RevalidateSession(Username: string, Password: string, AuthToken: string, SAPID: string, IsImpersonation: boolean) {
        return this.http
            .post<UserInfo>(this.configService.hostAddress + `Api/Services/Authentication/RevalidateSession`, {
                Username: Username || '',
                Password: Password || '',
                AuthToken: AuthToken || '',
                SAPID: SAPID || '',
                IsImpersonation: IsImpersonation || '',
            })
            .pipe(
                map((userinfo) => {
                    // If we have roles, before we return we should number them so we can recover in case of page refreshing.
                    if (userinfo.roles && !!userinfo.roles.length) {
                        userinfo.roles.forEach((role, idx) => {
                            role.roleNumber = idx;
                        });
                    }

                    return userinfo;
                })
            );
    }

    // #endregion

    // #region Start AuthToken Refreshing Service Call

    StartAuthTokenRefreshing() {
        // This process will work as follows: We will call a service method which will tell us if the current user's auth token shows
        // "recent use" (which is a flag set by most stored procedures). If that method succeeds, and shows recent use, we will simply
        // use a different service call to update the auth token refresh time, then delay for a time period (AUTHCHECKTIME) before we
        // run this same check again.
        // If at any point the recent use flag is not set, then we set a timer to logout the user after a set amount of time
        // (LOGOUTWARNINGTIME), and simultaneously put up a dialog box that gives the user a visual representation of the countdown,
        // along with a button they can click to "save" their session, which essentially manually triggers the refresh method.

        // Initiate the auth token refresh cycling by calling the refresh method the first time
        this.AuthTokenCancelFlag = false;

        // Check real quick for a present value for AuthRefreshDeadline. If that exists, then it indicates the time when we absolutely
        // need to refresh the auth token, so we should set the refresh handle time to just before then. Otherwise, this is probably a
        // first login so use the default time.
        let timeToRefresh = AuthService.AUTHCHECKTIME;

        if (this.nextAuthRefresh) {
            // Read the deadline time to refresh from the session storage, convert into a Date object.
            this.AuthRefreshDeadline = new Date(this.nextAuthRefresh);
        } else {
            // Nothing available from session storage, so queue up a fresh refresh clock.
            this.AuthRefreshDeadline = addSeconds(new Date(), AuthService.AUTHCHECKTIME);
        }

        if (this.AuthRefreshDeadline && isValid(this.AuthRefreshDeadline)) {
            // Find the time difference in seconds between now and when we are due to refresh.
            const timediff = differenceInSeconds(this.AuthRefreshDeadline, new Date());

            // We will always use the lesser of the next known time to refresh and the usual delay time. This is in case we are refreshing
            // the browser and need to restart refresh cycle, as opposed to fresh starting cycles.
            if (timediff < timeToRefresh) {
                timeToRefresh = timediff;
            }
            if (timeToRefresh <= 0) {
                timeToRefresh = 1;
            } // Ensure at least a 1 second wait.
        }

        // Begin the timer, if one isn't already set
        if (this._authtimer) {
            this._authtimer.StartTimer(timeToRefresh * 1000);
        }

        // Set the refresh deadline app value
        const Now = new Date();

        this.AuthRefreshDeadline = addSeconds(Now, timeToRefresh);
        this.IsRefreshing = true;
        this.nextAuthRefresh = this.toolsService.toISOLocal(this.AuthRefreshDeadline);
    }

    // #endregion

    // #region Test Connection Service Call

    TestConnection() {
        // Call the WebApi to see if anyone is home.
        return this.http.get<TestConnectionPacket>(this.configService.hostAddress + 'api/Login/connection');
    }

    // #endregion

    // #region Validate User Service Call

    ValidateUser(
        username: string,
        password: string,
        impersonatedUsername?: string,
        trainingMode?: boolean,
        screen?: string,
        winres?: string
    ) {
        return this.http
            .post<UserInfo>(this.configService.hostAddress + 'api/Login', {
                Username: username || '',
                Password: password || '',
                ImpersonatedUsername: impersonatedUsername || '',
                TrainingMode: trainingMode || '',
                Screen: screen,
                Window: winres || '',
            })
            .pipe(
                map((userinfo) => {
                    // If we have roles, before we return we should number them so we can recover in case of page refreshing.
                    if (userinfo.roles && !!userinfo.roles.length) {
                        userinfo.roles.forEach((role, idx) => {
                            role.roleNumber = idx;
                        });
                    }

                    return userinfo;
                })
            );
    }

    // #endregion

    Login(username: string, password: string) {
        // Authenticate and get a user information packet back
        return this.http
            .post<UserInfo>(
                this.configService.hostAddress + 'api/auth/authenticate',
                {
                    Username: username,
                    Password: password,
                },
                { withCredentials: true }
            )
            .pipe(
                map((userinfo) => {
                    // If we have roles, before we return we should number them so we can recover in case of page refreshing.
                    if (userinfo.roles && !!userinfo.roles.length) {
                        userinfo.roles.forEach((role, idx) => {
                            role.roleNumber = idx;
                        });
                    }

                    return userinfo;
                })
            );
    }
}
